import { useState, type FormEvent, type ReactNode } from 'react';

import { apiErrorMessage } from '@/api/errors';
import { Button, type ButtonProps } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Notice } from '@/components/ui/notice';

interface ConfirmDialogProps {
  triggerLabel: string;
  triggerVariant?: ButtonProps['variant'];
  title: string;
  description: ReactNode;
  confirmLabel: string;
  confirmVariant?: ButtonProps['variant'];
  children?: ReactNode;
  canConfirm?: boolean;
  onConfirm: () => Promise<unknown>;
  onOpenChange?: (open: boolean) => void;
}

export function ConfirmDialog({
  triggerLabel,
  triggerVariant = 'outline',
  title,
  description,
  confirmLabel,
  confirmVariant = 'default',
  children,
  canConfirm = true,
  onConfirm,
  onOpenChange,
}: ConfirmDialogProps) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changeOpen = (next: boolean) => {
    setOpen(next);
    setError(null);
    onOpenChange?.(next);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      await onConfirm();
      changeOpen(false);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant={triggerVariant}>
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          {children}
          {error && <Notice tone="destructive">{error}</Notice>}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => changeOpen(false)} disabled={pending}>
              Cancel
            </Button>
            <Button type="submit" variant={confirmVariant} disabled={pending || !canConfirm}>
              {pending ? 'Working…' : confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
